import { useEffect, useRef, useState } from "react";
import type { DevLogEvent } from "../api";
import { formatKathmanduClock } from "../lib/nepal-time";

function formatTime(ts: string): string {
  return formatKathmanduClock(ts);
}

function formatData(data: Record<string, unknown> | undefined): string {
  if (!data) return "";
  try {
    return JSON.stringify(data);
  } catch {
    return "";
  }
}

interface Props {
  title: string;
  subtitle: string;
  events: DevLogEvent[];
  expanded: boolean;
  onToggleExpand: () => void;
}

export function LogPane({ title, subtitle, events, expanded, onToggleExpand }: Props) {
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const el = scroller.current;
    if (!el || paused || !stick.current) return;
    el.scrollTop = el.scrollHeight;
  }, [events, paused]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    stick.current = atBottom;
    if (atBottom && paused) setPaused(false);
  };

  return (
    <section className={`log-pane ${expanded ? "is-full" : ""}`}>
      <header className="log-pane-head">
        <div>
          <h2>{title}</h2>
          <p>
            {subtitle} · {events.length} lines
            {paused ? " · paused" : ""}
          </p>
        </div>
        <div className="log-pane-actions">
          <button
            type="button"
            className="ghost"
            onClick={() => {
              setPaused((v) => {
                if (v) stick.current = true;
                return !v;
              });
            }}
          >
            {paused ? "Follow" : "Pause"}
          </button>
          <button type="button" className="ghost" onClick={onToggleExpand}>
            {expanded ? "Restore" : "Full screen"}
          </button>
        </div>
      </header>
      <div className="log-scroll" ref={scroller} onScroll={onScroll}>
        {events.length === 0 ? (
          <div className="log-empty">Waiting for logs…</div>
        ) : (
          events.map((event, i) => {
            const extra = formatData(event.data);
            return (
              <div key={`${event.ts}-${event.stream}-${i}`} className={`log-line lv-${event.level}`}>
                <span className="log-time">{formatTime(event.ts)}</span>
                <span className="log-level">{event.level.toUpperCase().padEnd(5, " ")}</span>
                <span className="log-svc">{event.service}</span>
                <span className="log-msg">
                  {event.msg}
                  {extra ? ` ${extra}` : ""}
                </span>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
